import { useEffect, useRef, type ReactNode } from "react";
import { useFormikContext } from "formik";
import { Prompt } from "react-router";
import { ExternalLink, Gem } from "lucide-react";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { usePermissions } from "components/permissions/usePermissions";
import { Badge } from "components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import { FieldError } from "components/ui/field";
import { ModifyApiKey } from "data/models";
import { cn } from "lib/utils";
import * as Routes from "routes";
import { API_GROUPS, type ApiProductOption } from "./api-product-options";

// TODO: Set this to true when Verify launches to enable its checkboxes on create and edit.
const VERIFY_SELECTION_ENABLED = false;

export function FocusValidationErrors({
  formRef,
}: {
  formRef: React.RefObject<HTMLFormElement>;
}) {
  const { submitCount, isSubmitting, isValidating } =
    useFormikContext<ModifyApiKey>();
  const handledSubmit = useRef(0);

  useEffect(() => {
    if (isSubmitting || isValidating || submitCount === handledSubmit.current)
      return;
    handledSubmit.current = submitCount;
    const firstInvalidField = formRef.current?.querySelector<HTMLElement>(
      '[aria-invalid="true"]',
    );
    firstInvalidField?.focus({ preventScroll: true });
    firstInvalidField?.scrollIntoView({ block: "center" });
  }, [submitCount, isSubmitting, isValidating, formRef]);

  return null;
}

export function ApiSelectionSummary({
  options,
}: {
  options: ApiProductOption[];
}) {
  const { values } = useFormikContext<ModifyApiKey>();
  const selectedProducts = new Map<string, string>();
  for (const option of options) {
    if (values.products.includes(option.product.name)) {
      selectedProducts.set(
        option.product.name,
        option.group === "Geoscape Verify" ? "Geoscape Verify" : option.title,
      );
    }
  }

  return (
    <div
      className="min-w-0 space-y-2"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <p className="text-sm font-medium">
        {selectedProducts.size === 0
          ? "No APIs selected"
          : `${selectedProducts.size} API${selectedProducts.size === 1 ? "" : "s"} selected`}
      </p>
      {selectedProducts.size > 0 && (
        <div className="flex flex-wrap gap-2">
          {Array.from(selectedProducts, ([name, title]) => (
            <Badge key={name} variant="secondary">
              {title}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppFormActionBar({
  children,
  isSaving,
  pendingStatus,
  initialStatus,
}: {
  children: ReactNode;
  isSaving: boolean;
  pendingStatus: string;
  initialStatus: string;
}) {
  const { dirty } = useFormikContext<ModifyApiKey>();

  return (
    <div
      role="region"
      aria-label="App actions"
      className="sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-3 border-t bg-background px-4 py-3 shadow-[0_-4px_12px_rgb(0_0_0_/_0.04)]"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <p
        className="text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        {isSaving ? pendingStatus : dirty ? "Unsaved changes" : initialStatus}
      </p>
      <div className="ml-auto flex shrink-0 items-center gap-3">{children}</div>
    </div>
  );
}

export function UnsavedChangesWarning({
  saved,
  isSaving,
  message = "Your app has not been created. If you leave, your changes will be lost.",
}: {
  saved: React.MutableRefObject<boolean>;
  isSaving: boolean;
  message?: string;
}) {
  const { dirty } = useFormikContext<ModifyApiKey>();
  useEffect(() => {
    if (!dirty) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (saved.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty, saved]);

  return (
    <Prompt
      when={dirty}
      message={() => {
        if (saved.current) return true;
        if (isSaving) return false;
        return message;
      }}
    />
  );
}

export function ApiAccessSelection({
  options,
  isSaving,
  readOnly = false,
  allowExistingUnavailable = false,
}: {
  options: ApiProductOption[];
  isSaving: boolean;
  readOnly?: boolean;
  allowExistingUnavailable?: boolean;
}) {
  const {
    values,
    initialValues,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ModifyApiKey>();
  const { isPermitted, permissionDeniedMessageString } = usePermissions();
  const premiumPermissions = [PermissionsKeys.premiumDataRead];
  const canAccessPremium = isPermitted(premiumPermissions);
  return (
    <Card
      id="api-access"
      tabIndex={-1}
      aria-label="API Access"
      aria-invalid={touched.products && !!errors.products}
      aria-describedby={
        touched.products && errors.products ? "api-selection-error" : undefined
      }
      className="outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <CardHeader>
        <CardTitle>API Access</CardTitle>
        <CardDescription>
          Select at least one API this app can access.
        </CardDescription>
        <a
          href={Routes.docsApi}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-1 text-sm text-primary underline underline-offset-4"
        >
          Explore API documentation
          <ExternalLink className="size-3" />
        </a>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="border-b pb-4">
          <ApiSelectionSummary options={options} />
        </div>
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Gem className="size-4 shrink-0 text-primary" aria-hidden="true" />
          <span>
            Premium APIs require a paid subscription plan. Partly premium APIs
            include some calls or functions that require a paid subscription
            plan.
          </span>
        </p>
        {API_GROUPS.map((group) => {
          const groupOptions = options.filter(
            (option) => option.group === group,
          );
          if (!groupOptions.length) return null;
          return (
            <fieldset
              key={group}
              className="space-y-3"
              aria-describedby={
                group === "Geoscape Verify" ? "verify-help" : undefined
              }
            >
              <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {group}
                {group === "Geoscape Verify" && (
                  <Badge
                    variant="secondary"
                    className="ml-2 normal-case tracking-normal"
                  >
                    Enabled together
                  </Badge>
                )}
              </legend>
              {group === "Geoscape Verify" && (
                <p id="verify-help" className="text-xs text-muted-foreground">
                  {VERIFY_SELECTION_ENABLED
                    ? "Selecting or clearing any Verify service applies to all five. Your plan determines which features you can use."
                    : "Coming soon. Verify API selection will be available at launch. Existing app access is preserved."}
                </p>
              )}
              {group === "Legacy" && (
                <p className="text-xs text-muted-foreground">
                  {VERIFY_SELECTION_ENABLED
                    ? "For existing integrations. For new address integrations, choose Geoscape Verify."
                    : "For existing integrations. Geoscape Verify is coming soon for new address integrations."}
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {groupOptions.map(
                  ({
                    id,
                    product,
                    title,
                    description,
                    icon: Icon,
                    premium,
                  }) => {
                    const checked = values.products.includes(product.name);
                    const unavailable =
                      product.premium === "full" && !canAccessPremium;
                    const existingAccess =
                      allowExistingUnavailable &&
                      initialValues.products.includes(product.name);
                    const disabled =
                      readOnly ||
                      isSaving ||
                      (group === "Geoscape Verify" && !VERIFY_SELECTION_ENABLED) ||
                      (unavailable && !existingAccess);
                    return (
                      <label
                        key={id}
                        htmlFor={id}
                        aria-disabled={disabled}
                        className={cn(
                          "flex items-start gap-3 rounded-lg border p-3 transition-colors",
                          checked
                            ? "border-primary bg-primary/10 ring-1 ring-primary/30"
                            : "border-border",
                          disabled
                            ? "cursor-not-allowed bg-muted/50 opacity-50"
                            : "cursor-pointer hover:bg-muted/50",
                        )}
                      >
                        <Checkbox
                          id={id}
                          checked={checked}
                          disabled={disabled}
                          className="mt-0.5"
                          aria-label={title}
                          aria-describedby={`${id}-description${premium ? ` ${id}-premium` : ""}`}
                          onCheckedChange={(selected) => {
                            setFieldValue(
                              "products",
                              selected === true
                                ? Array.from(
                                    new Set([...values.products, product.name]),
                                  )
                                : values.products.filter(
                                    (name) => name !== product.name,
                                  ),
                            );
                            setFieldTouched("products", true, false);
                          }}
                        />
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="flex items-center gap-2 text-sm font-medium">
                              <Icon className="size-4 shrink-0 text-muted-foreground" />
                              {title}
                            </span>
                            {premium && (
                              <span
                                id={`${id}-premium`}
                                className="ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground"
                              >
                                <Gem
                                  className="size-3 text-primary"
                                  aria-hidden="true"
                                />
                                {premium === "full"
                                  ? "Premium"
                                  : "Partly premium"}
                              </span>
                            )}
                          </div>
                          <p
                            id={`${id}-description`}
                            className="text-xs text-muted-foreground"
                          >
                            {description}
                          </p>
                          {unavailable && (
                            <p className="text-xs text-muted-foreground">
                              {permissionDeniedMessageString(
                                premiumPermissions,
                              )}
                            </p>
                          )}
                          {premium === "partial" && !canAccessPremium && (
                            <p className="text-xs text-muted-foreground">
                              Some calls or functions require a paid
                              subscription plan.
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  },
                )}
              </div>
            </fieldset>
          );
        })}
        {touched.products && errors.products && (
          <FieldError id="api-selection-error">{errors.products}</FieldError>
        )}
        <p className="text-xs text-muted-foreground">
          You can change API access later in the app settings.
        </p>
      </CardContent>
    </Card>
  );
}
